<template>
  <div class="NorceCheckoutNonPsp">
    <!-- Loading state -->
    <div v-if="isInitializing" class="nonpsp-loading flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-text-subtle">{{ $t('checkout.norceCheckout.loading') }}</span>
    </div>

    <!-- Error state -->
    <div v-else-if="error && !payment" class="nonpsp-error p-16 bg-bg-error rounded-8">
      <p class="type-sm text-text-error">{{ error }}</p>
      <button
        class="btn btn--secondary mt-16"
        type="button"
        @click="retryInitialize"
      >
        {{ $t('checkout.norceCheckout.retry') }}
      </button>
    </div>

    <!-- NonPsp Payment content -->
    <div v-else-if="nonPspPayment" class="nonpsp-content">
      <!-- Payment method selector (when multiple methods available) -->
      <div v-if="nonPspPaymentMethods.length > 1" class="mb-16">
        <p class="type-headline-sm mb-8">{{ $t('checkout.norceCheckout.selectPaymentMethod') }}</p>
        <div class="flex flex-col gap-8">
          <label
            v-for="method in nonPspPaymentMethods"
            :key="method.identifier"
            class="flex items-center gap-8 p-12 border cursor-pointer"
            :class="{
              'border-border-base': selectedMethod === method.identifier,
              'border-border-subtle': selectedMethod !== method.identifier,
            }"
          >
            <input
              v-model="selectedMethod"
              type="radio"
              :value="method.identifier"
              name="nonpsp-method"
            >
            <span class="type-sm">{{ method.name || method.identifier }}</span>
          </label>
        </div>
      </div>

      <!-- Complete button -->
      <button
        class="btn w-full"
        :class="{ loading: isProcessing }"
        :disabled="isProcessing || !canComplete"
        type="button"
        @click="handleComplete"
      >
        {{ $t('checkout.norceCheckout.completeOrder') }}
      </button>

      <!-- Error message -->
      <div v-if="error" class="mt-16 p-16 bg-bg-error rounded-8">
        <p class="type-sm text-text-error">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import useNorceCheckout from './useNorceCheckout';
import { buildSuccessUrl } from './usePaymentSession';
import useLinkReplacer from '~/composables/useLinkReplacer';
import type { NonPspPaymentData } from './types';

const props = withDefaults(defineProps<{
  basketUuid: string;
  canCompletePurchase?: boolean;
  autoInitialize?: boolean;
}>(), {
  canCompletePurchase: true,
  autoInitialize: true,
});

const emit = defineEmits<{
  (e: 'success', basketId: string): void;
  (e: 'error', error: string): void;
}>();

const { $t } = useNuxtApp();
const { manualUrl } = useLinkReplacer();

const {
  orderId,
  paymentId,
  payment,
  isInitializing,
  isProcessing,
  error,
  isNonPsp,
  initialize,
  selectNonPspMethod,
  completeNonPsp,
  reset,
} = useNorceCheckout();

// Local state
const selectedMethod = ref<string | null>(null);

// Typed payment helper
const nonPspPayment = computed(() => isNonPsp.value ? payment.value as NonPspPaymentData : null);

// Available payment methods
const nonPspPaymentMethods = computed(() => nonPspPayment.value?.paymentMethods || []);

// Can complete payment
const canComplete = computed(() => {
  if (!props.canCompletePurchase) { return false; }
  if (nonPspPaymentMethods.value.length === 0) { return true; }
  if (nonPspPaymentMethods.value.length === 1) { return true; }
  return selectedMethod.value !== null;
});

/**
 * Initialize the NonPsp checkout
 */
const initializeNonPsp = async() => {
  if (!props.basketUuid) { return; }

  const success = await initialize(props.basketUuid);

  if (success && nonPspPayment.value) {
    // Auto-select if pre-selected or only one method
    if (nonPspPayment.value.selectedPaymentMethod) {
      selectedMethod.value = nonPspPayment.value.selectedPaymentMethod;
    } else if (nonPspPaymentMethods.value.length === 1) {
      selectedMethod.value = nonPspPaymentMethods.value[0].identifier;
    }
  }
};

/**
 * Retry initialization
 */
const retryInitialize = () => {
  reset();
  selectedMethod.value = null;
  initializeNonPsp();
};

/**
 * Handle payment completion
 */
const handleComplete = async() => {
  const methodToSelect = selectedMethod.value ||
    (nonPspPaymentMethods.value.length === 1 ? nonPspPaymentMethods.value[0].identifier : null);

  // Only call selectNonPspMethod if the method differs from what's already selected on backend
  const alreadySelected = nonPspPayment.value?.selectedPaymentMethod;
  if (methodToSelect && methodToSelect !== alreadySelected) {
    const selectSuccess = await selectNonPspMethod(methodToSelect);
    if (!selectSuccess) {
      emit('error', error.value || 'Failed to select payment method');
      return;
    }
  }

  // Complete the payment
  const result = await completeNonPsp();

  if (result.success && result.order) {
    emit('success', result.order.basketId);
    // Navigate to success page
    const successUrl = buildSuccessUrl(manualUrl(''), result.order.basketId, {
      adapter: 'nonpsp',
      orderId: orderId.value,
      paymentId: paymentId.value,
    });
    await navigateTo(successUrl);
  } else {
    emit('error', error.value || 'Failed to complete order');
  }
};

// Watch for payment data changes to auto-select method
watch(nonPspPayment, (newPayment) => {
  if (newPayment && !selectedMethod.value) {
    if (newPayment.selectedPaymentMethod) {
      selectedMethod.value = newPayment.selectedPaymentMethod;
    } else if (nonPspPaymentMethods.value.length === 1) {
      selectedMethod.value = nonPspPaymentMethods.value[0].identifier;
    }
  }
});

onMounted(() => {
  if (props.autoInitialize) {
    initializeNonPsp();
  } else if (nonPspPayment.value) {
    // Data already available, just set up selection
    if (nonPspPayment.value.selectedPaymentMethod) {
      selectedMethod.value = nonPspPayment.value.selectedPaymentMethod;
    } else if (nonPspPaymentMethods.value.length === 1) {
      selectedMethod.value = nonPspPaymentMethods.value[0].identifier;
    }
  }
});

// Expose methods for parent component
defineExpose({
  initialize: initializeNonPsp,
  retry: retryInitialize,
});
</script>

<style scoped lang="postcss">
.NorceCheckoutNonPsp {
  @apply w-full;
}

.loading-spinner {
  @apply w-24 h-24 border-2 border-border-subtle border-t-border-base animate-spin;
  border-radius: 50%;
}
</style>
